import { isArabicUi } from "@/shared/config/arabicUi";
import { NavStringKey, NavStrings, navStrings } from "./strings";

/**
 * Строки «Тренировок» сайта без приложения — карточка
 * приложения, тестовая тренировка, ссылка на лендинг — на всех пяти языках
 * сайта (ru, en, de, es, cn; арабский — при включённом арабском интерфейсе,
 * иначе, как и всё прочее, — английский).
 *
 * Свой словарь, а не strings.ts меню V5: там пока только ru и en. Ключи
 * карточки, которые есть и в меню (CardKey), здесь перекрывают его строки на
 * всех языках (promoNavStrings) — карточка читает их по-старому, s("…").
 * Новые — только здесь (promoStrings).
 *
 * Где у приложения есть своя строка (res/values-* /strings.xml) — дословно
 * она: «Пульсометр», «Таймер поста», «Тестовые тренировки», «Динамика»…
 */
type CardKey = Extract<
    NavStringKey,
    | "training"
    | "calendar"
    | "appCardTitle"
    | "appPreviewTraining"
    | "appPreviewPlan"
    | "appPreviewLabel"
    | "planTitle"
    | "planSub"
    | "planChip"
    | "featSensors"
    | "featFastTimer"
    | "featWatches"
    | "featTests"
    | "install"
    | "installShort"
    | "openInApp"
    | "uploadNote"
    | "oldVersion"
    | "oldCalendar"
    | "oldTesting"
    | "oldPlan"
    | "oldUpload"
>;

type NewKey =
    | "featThermo"
    | "collapsedTitle"
    // «Календарь» нижней панели (владелец, 20.10): та же карточка, свой заголовок.
    | "calendarCardTitle"
    | "calendarCollapsedTitle"
    // Короткая кнопка свёрнутой полосы, когда приложение на телефоне есть (useHasApp).
    | "openShort"
    | "landingTitle"
    | "landingCta"
    | "collapse"
    | "expand"
    | "testTitle"
    | "testSub"
    | "testAdd";

export type PromoKey = CardKey | NewKey;
type Dict = Record<PromoKey, string>;

const RU: Dict = {
    training: "Тренировки",
    calendar: "Календарь",
    appCardTitle: "Тренировки и план — в приложении MY REVIVE",
    collapsedTitle: "Тренировки и план — в приложении",
    calendarCardTitle: "Календарь и история тренировок — в приложении MY REVIVE",
    calendarCollapsedTitle: "Календарь и история — в приложении",
    openShort: "Открыть",
    landingTitle: "Метод целиком — в приложении",
    landingCta: "Посмотреть, как это работает",
    appPreviewTraining: "Тренировки",
    appPreviewPlan: "План — по дням",
    appPreviewLabel:
        "Так это выглядит в приложении: список тренировок, в нём «Конструктор плана», и план по дням",
    planTitle: "План тренировок — в приложении",
    planSub: "с годовой подпиской или с обеими частями курса «Восстановление жизненных сил»",
    planChip: "тренировки за неделю по плану",
    featSensors: "Пульсометр",
    featThermo: "Датчик температуры и влажности",
    featWatches: "Часы Garmin, Wear OS и Apple Watch",
    featFastTimer: "Таймер поста",
    featTests: "Тестовые тренировки",
    install: "Установить приложение",
    installShort: "Установить",
    openInApp: "Открыть в приложении MY REVIVE",
    collapse: "Свернуть",
    expand: "Развернуть",
    testTitle: "Тестовая тренировка",
    testSub: "снимок до и после",
    testAdd: "Добавить",
    uploadNote: "Файлы с часов (.fit, .gpx, .tcx) — загружаются в приложении",
    oldVersion: "старая версия",
    oldCalendar: "календарь",
    oldTesting: "тестовая тренировка",
    oldPlan: "план",
    oldUpload: "загрузка файла",
};

const EN: Dict = {
    training: "Workouts",
    calendar: "Calendar",
    appCardTitle: "Workouts and plan — in the MY REVIVE app",
    collapsedTitle: "Workouts and plan — in the app",
    calendarCardTitle: "Calendar and workout history — in the MY REVIVE app",
    calendarCollapsedTitle: "Calendar and history — in the app",
    openShort: "Open",
    landingTitle: "The whole method — in the app",
    landingCta: "See how it works",
    appPreviewTraining: "Workouts",
    appPreviewPlan: "Plan — by day",
    appPreviewLabel:
        "How it looks in the app: the workout list with the “Plan builder”, and the plan by day",
    planTitle: "Workout plan — in the app",
    planSub: "with the annual subscription or with both parts of the “Restoring vital energy” course",
    planChip: "workout points for a week on plan",
    featSensors: "Heart rate monitor",
    featThermo: "Temperature & humidity sensor",
    featWatches: "Garmin, Wear OS and Apple Watch",
    featFastTimer: "Fasting timer",
    featTests: "Test workouts",
    install: "Install the app",
    installShort: "Install",
    openInApp: "Open in the MY REVIVE app",
    collapse: "Collapse",
    expand: "Expand",
    testTitle: "Test workout",
    testSub: "a photo before and after",
    testAdd: "Add",
    uploadNote: "Watch files (.fit, .gpx, .tcx) — upload them in the app",
    oldVersion: "old version",
    oldCalendar: "calendar",
    oldTesting: "test workout",
    oldPlan: "plan",
    oldUpload: "file upload",
};

const DE: Dict = {
    training: "Trainings",
    calendar: "Kalender",
    appCardTitle: "Trainings und Plan — in der App MY REVIVE",
    collapsedTitle: "Trainings und Plan — in der App",
    calendarCardTitle: "Kalender und Trainingsverlauf — in der App MY REVIVE",
    calendarCollapsedTitle: "Kalender und Verlauf — in der App",
    openShort: "Öffnen",
    landingTitle: "Die ganze Methode — in der App",
    landingCta: "So funktioniert es",
    appPreviewTraining: "Trainings",
    appPreviewPlan: "Plan — nach Tagen",
    appPreviewLabel:
        "So sieht es in der App aus: die Trainingsliste mit dem „Plan-Konstruktor“ und der Plan nach Tagen",
    planTitle: "Trainingsplan — in der App",
    planSub: "mit dem Jahresabo oder mit beiden Teilen des Kurses „Wiederherstellung der Lebenskraft“",
    planChip: "Punkte für eine Woche nach Plan",
    featSensors: "Pulsmesser",
    featThermo: "Temperatur- und Feuchtigkeitssensor",
    featWatches: "Uhren: Garmin, Wear OS, Apple Watch",
    featFastTimer: "Fasten-Timer",
    featTests: "Testtrainings",
    install: "App installieren",
    installShort: "Installieren",
    openInApp: "In der App MY REVIVE öffnen",
    collapse: "Einklappen",
    expand: "Ausklappen",
    testTitle: "Testtraining",
    testSub: "ein Foto davor und danach",
    testAdd: "Hinzufügen",
    uploadNote: "Dateien von der Uhr (.fit, .gpx, .tcx) — in der App hochladen",
    oldVersion: "alte Version",
    oldCalendar: "Kalender",
    oldTesting: "Testtraining",
    oldPlan: "Plan",
    oldUpload: "Datei hochladen",
};

const ES: Dict = {
    training: "Entrenamientos",
    calendar: "Calendario",
    appCardTitle: "Entrenamientos y plan — en la app MY REVIVE",
    collapsedTitle: "Entrenamientos y plan — en la app",
    calendarCardTitle: "Calendario e historial de entrenamientos — en la app MY REVIVE",
    calendarCollapsedTitle: "Calendario e historial — en la app",
    openShort: "Abrir",
    landingTitle: "Todo el método — en la app",
    landingCta: "Ver cómo funciona",
    appPreviewTraining: "Entrenamientos",
    appPreviewPlan: "Plan — por días",
    appPreviewLabel:
        "Así se ve en la app: la lista de entrenamientos con el «Constructor de plan» y el plan por días",
    planTitle: "Plan de entrenamientos — en la app",
    planSub: "con la suscripción anual o con las dos partes del curso «Restauración de la energía vital»",
    planChip: "puntos por una semana con plan",
    featSensors: "Pulsómetro",
    featThermo: "Sensor de temperatura y humedad",
    featWatches: "Relojes Garmin, Wear OS y Apple Watch",
    featFastTimer: "Temporizador de ayuno",
    featTests: "Entrenamientos de prueba",
    install: "Instalar la app",
    installShort: "Instalar",
    openInApp: "Abrir en la app MY REVIVE",
    collapse: "Contraer",
    expand: "Desplegar",
    testTitle: "Entrenamiento de prueba",
    testSub: "una foto antes y después",
    testAdd: "Añadir",
    uploadNote: "Archivos del reloj (.fit, .gpx, .tcx): súbelos en la app",
    oldVersion: "versión anterior",
    oldCalendar: "calendario",
    oldTesting: "entrenamiento de prueba",
    oldPlan: "plan",
    oldUpload: "subir archivo",
};

const CN: Dict = {
    training: "训练",
    calendar: "日历",
    appCardTitle: "训练和计划 — 在 MY REVIVE 应用中",
    collapsedTitle: "训练和计划 — 在应用中",
    calendarCardTitle: "日历和训练记录 — 在 MY REVIVE 应用中",
    calendarCollapsedTitle: "日历和记录 — 在应用中",
    openShort: "打开",
    landingTitle: "完整的方法 — 尽在应用中",
    landingCta: "看看它如何运作",
    appPreviewTraining: "训练",
    appPreviewPlan: "计划 — 按天",
    appPreviewLabel: "应用中的样子：训练列表中的“计划构建器”，以及按天排列的计划",
    planTitle: "训练计划 — 在应用中",
    planSub: "年度订阅或“恢复生命力”课程的两个部分",
    planChip: "按计划完成一周的训练积分",
    featSensors: "心率监测器",
    featThermo: "温湿度传感器",
    featWatches: "Garmin、Wear OS 和 Apple Watch 手表",
    featFastTimer: "断食计时器",
    featTests: "测试训练",
    install: "安装应用",
    installShort: "安装",
    openInApp: "在 MY REVIVE 应用中打开",
    collapse: "收起",
    expand: "展开",
    testTitle: "测试训练",
    testSub: "训练前后各一张照片",
    testAdd: "添加",
    uploadNote: "手表文件（.fit、.gpx、.tcx）— 在应用中上传",
    oldVersion: "旧版本",
    oldCalendar: "日历",
    oldTesting: "测试训练",
    oldPlan: "计划",
    oldUpload: "上传文件",
};

/** Арабский — только при включённом арабском интерфейсе (shared/config/arabicUi). */
const AR: Dict = {
    training: "التمارين",
    calendar: "التقويم",
    appCardTitle: "التمارين والخطة — في تطبيق MY REVIVE",
    collapsedTitle: "التمارين والخطة — في التطبيق",
    calendarCardTitle: "التقويم وسجل التمارين — في تطبيق MY REVIVE",
    calendarCollapsedTitle: "التقويم والسجل — في التطبيق",
    openShort: "فتح",
    landingTitle: "المنهج كاملًا — في التطبيق",
    landingCta: "شاهد كيف يعمل",
    appPreviewTraining: "التمارين",
    appPreviewPlan: "الخطة — بالأيام",
    appPreviewLabel: "كيف يبدو في التطبيق: قائمة التمارين مع «منشئ الخطة»، والخطة بالأيام",
    planTitle: "خطة التمارين — في التطبيق",
    planSub: "مع الاشتراك السنوي أو مع جزأي دورة «استعادة الطاقة الحيوية»",
    planChip: "نقاط التمارين عن أسبوع وفق الخطة",
    featSensors: "مقياس النبض",
    featThermo: "مستشعر الحرارة والرطوبة",
    featWatches: "Garmin وWear OS وApple Watch",
    featFastTimer: "مؤقّت الصيام",
    featTests: "التمارين التجريبية",
    install: "ثبّت التطبيق",
    installShort: "تثبيت",
    openInApp: "افتح في تطبيق MY REVIVE",
    collapse: "طيّ",
    expand: "توسيع",
    testTitle: "تمرين تجريبي",
    testSub: "صورة قبل وبعد",
    testAdd: "إضافة",
    uploadNote: "ملفات الساعة (fit وgpx وtcx) — ارفعها في التطبيق",
    oldVersion: "النسخة القديمة",
    oldCalendar: "التقويم",
    oldTesting: "تمرين تجريبي",
    oldPlan: "الخطة",
    oldUpload: "رفع ملف",
};

export type PromoLang = "ru" | "en" | "de" | "es" | "cn" | "ar";

export const PROMO_DICTS: Readonly<Record<PromoLang, Dict>> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, ar: AR };

/**
 * Язык сайта → язык словаря: zh — это cn сайта; ar — арабский при включённом
 * арабском интерфейсе (рубильник или предпросмотр), иначе, как и незнакомое, — en.
 */
export const promoLang = (siteLang: string | undefined | null): PromoLang => {
    const l = (siteLang || "").toLowerCase();
    if (l === "zh") return "cn";
    if (isArabicUi(l)) return "ar";
    return l === "ru" || l === "en" || l === "de" || l === "es" || l === "cn" ? l : "en";
};

export type PromoStrings = (key: PromoKey) => string;

/** Строки карточки на языке страницы. */
export const promoStrings = (siteLang: string | undefined | null): PromoStrings => {
    const dict = PROMO_DICTS[promoLang(siteLang)];
    return (key) => dict[key];
};

const CARD_KEYS: ReadonlySet<string> = new Set(Object.keys(RU));

/**
 * Строки меню V5, где ключи карточки (заголовок, план, «что умеет»,
 * «Установить»…) — из этого словаря на всех языках сайта; остальное — как в
 * меню.
 */
export const promoNavStrings = (siteLang: string | undefined | null): NavStrings => {
    const promo = PROMO_DICTS[promoLang(siteLang)];
    const nav = navStrings(siteLang || "ru");
    return (key, vars) => (CARD_KEYS.has(key) ? promo[key as PromoKey] : nav(key, vars));
};
